import { isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import type { FinanceTransaction } from '@/types/finance';
import { KIND_MAPPING, type ReportFilters, type ReportKind } from './types';

export function filterTransactionsForReport(
  transactions: FinanceTransaction[],
  kind: ReportKind,
  filters: ReportFilters,
): FinanceTransaction[] {
  const mapping = KIND_MAPPING[kind];
  const interval = {
    start: startOfDay(filters.fromDate),
    end: endOfDay(filters.toDate),
  };

  return transactions.filter((t) => {
    if (t.type !== mapping.type) return false;
    if (t.status !== mapping.status) return false;
    const dateValue = mapping.dateField === 'paymentDate' ? t.paymentDate : t.dueDate;
    if (!dateValue) return false;
    if (!isWithinInterval(dateValue, interval)) return false;
    if (filters.accountId && t.accountId !== filters.accountId) return false;
    if (filters.counterpartyId && t.counterpartyId !== filters.counterpartyId) return false;
    if (filters.costCenterId) {
      // Match também se algum split aponta para esse centro de custo.
      const directMatch = t.costCenterId === filters.costCenterId;
      const splitMatch =
        !!t.costCenterSplits &&
        t.costCenterSplits.some((s) => s.costCenterId === filters.costCenterId);
      if (!directMatch && !splitMatch) return false;
    }
    if (filters.categoryId) {
      // Match também se algum split aponta para essa categoria.
      const directMatch = t.categoryId === filters.categoryId;
      const splitMatch =
        !!t.categorySplits &&
        t.categorySplits.some((s) => s.categoryId === filters.categoryId);
      if (!directMatch && !splitMatch) return false;
    }
    return true;
  });
}

export interface ReportSummary {
  totalAmount: number;
  count: number;
  averageAmount: number;
}

export function summarizeRows(rows: FinanceTransaction[]): ReportSummary {
  // Soma o total efetivo (principal + multa + juros) já que multa/juros vão
  // para a mesma categoria do lançamento e fazem parte do que entrou/saiu.
  const totalAmount = rows.reduce(
    (sum, r) => sum + Number(r.amount) + Number(r.interest ?? 0) + Number(r.fine ?? 0),
    0,
  );
  const count = rows.length;
  return {
    totalAmount,
    count,
    averageAmount: count > 0 ? totalAmount / count : 0,
  };
}
